import { ConversationDTO, ConversationMessage, RepliedToMessage } from 'services/api/models/ConversationDTO';
import { UserDTO } from 'services/api/models/UserDTO';

/**
 * Pure functions that produce the next conversations-list state from the previous one.
 * They are consumed by the shell websocket handler and by components that need to
 * optimistically update the TanStack `['conversations']` cache. Keeping them as pure
 * `(previous) => next` functions makes them trivial to drive from `setQueryData` and
 * unit-test-friendly.
 */

/**
 * Append a new message to the conversation row's `last_message` (and bump the
 * unread counter when requested). No-op when the conversation isn't already in the
 * list — the websocket-handled events that introduce a new conversation
 * (`conversation.created_by_patient`, `conversation.became_visible`, `unassigned_message`) take care
 * of inserting it.
 */
export const updateConversationWithNewMessage =
  (payload: {
    messageText: string;
    sender: UserDTO;
    conversationId: string;
    messageId?: number;
    incrementUnreadMessages?: boolean;
    sentAt?: string;
  }) =>
  (previous: ConversationDTO[]): ConversationDTO[] => {
    const { messageText, sender, conversationId, messageId, incrementUnreadMessages, sentAt } = payload;
    const ts = sentAt ?? new Date().toISOString();
    return previous.map(conversation => {
      if (conversation.id !== conversationId) return conversation;
      return {
        ...conversation,
        last_message: {
          // messageId may be absent in the purely-optimistic path (before the
          // server responds), but when called from the WS handler the real DB id
          // is always available and must be stored so that subsequent
          // updateConversationLastMessageIfMatch calls (for edits/deletes) can
          // match the row correctly.
          id: messageId,
          message_text: messageText,
          sender,
          sent_at: ts,
        } as unknown as ConversationDTO['last_message'],
        ...(incrementUnreadMessages
          ? { number_of_unread_messages: (conversation.number_of_unread_messages ?? 0) + 1 }
          : {}),
        ...(sender?.id === conversation.patient?.id ? { patient_has_written: true } : {}),
      };
    });
  };

/**
 * Track that `reader` has read the conversation up to `lastReadDatetime`. The patient
 * UI uses these read statuses to drive the "delivered/read" check marks; they are not
 * the same thing as `number_of_unread_messages`.
 */
export const updateConversationReadStatus =
  (payload: { conversationId: string; reader: string; lastReadDatetime: string }) =>
  (previous: ConversationDTO[]): ConversationDTO[] => {
    const { conversationId, reader, lastReadDatetime } = payload;
    return previous.map(conversation => {
      if (conversation.id !== conversationId) return conversation;
      const updated = (conversation.read_statuses ?? [])
        .filter(s => s.reader !== reader)
        .concat([{ reader, last_read_datetime: lastReadDatetime }]);
      return { ...conversation, read_statuses: updated };
    });
  };

/** Replace the row's `status.value`. */
export const setConversationStatusValue =
  (conversationId: string, statusValue: string) =>
  (previous: ConversationDTO[]): ConversationDTO[] => {
    return previous.map(conversation => {
      if (conversation.id !== conversationId) return conversation;
      return {
        ...conversation,
        status: { ...(conversation.status ?? { value: statusValue }), value: statusValue },
        latest_status_change_datetime: new Date().toISOString(),
      };
    });
  };

/**
 * Optimistically reflect a caregiver-specific assignment being reverted back
 * to first-come-first-served (pushed back via "Refuser", GYN-264) — sets the
 * new status value and clears the now-stale `registered_caregiver`, without
 * removing the row from the list (it stays visible, just no longer "assigned
 * to me"). This is safe to apply ahead of the server response: the
 * subsequent `conversation.assignment.rejected` / `.timed_out` websocket
 * event upserts the authoritative full row from the server (filter-by-id
 * then prepend), so whichever of the two arrives first or second, the list
 * converges to the same correct end state.
 */
export const setConversationRejectedFromAssignment =
  (conversationId: string, statusValue: string) =>
  (previous: ConversationDTO[]): ConversationDTO[] => {
    return previous.map(conversation => {
      if (conversation.id !== conversationId) return conversation;
      return {
        ...conversation,
        status: {
          ...(conversation.status ?? { value: statusValue }),
          value: statusValue,
          registered_caregiver: undefined,
        },
      };
    });
  };

/** Set the `current_owner` of an existing row. No-op when the row isn't there. */
export const setOwnerForConversation =
  (conversationId: string, currentOwner: UserDTO) =>
  (previous: ConversationDTO[]): ConversationDTO[] => {
    return previous.map(conversation => {
      if (conversation.id !== conversationId) return conversation;
      return { ...conversation, current_owner: currentOwner };
    });
  };

/** Drop a row from the list. */
export const removeConversation =
  (conversationId: string) =>
  (previous: ConversationDTO[]): ConversationDTO[] => {
    return previous.filter(c => c.id !== conversationId);
  };

/** Reset the unread counter to 0 (typically after the user opens the conversation). */
export const resetUnreadMessages =
  (conversationId: string) =>
  (previous: ConversationDTO[]): ConversationDTO[] => {
    return previous.map(conversation => {
      if (conversation.id !== conversationId) return conversation;
      return { ...conversation, number_of_unread_messages: 0 };
    });
  };

/**
 * Idempotently insert a new row at the top of the list. Used when the patient creates
 * their own conversation, when an oriented conversation gets its first message, etc.
 * Returns the list unchanged if the row is already there.
 */
export const insertConversation =
  (conversation: ConversationDTO) =>
  (previous: ConversationDTO[]): ConversationDTO[] => {
    if (previous.some(c => c.id === conversation.id)) return previous;
    return [conversation, ...previous];
  };

/**
 * Replace an existing row with the incoming data, or insert it at the top if it
 * isn't in the list yet. Used for `questionnaire.completed` WS events where the
 * backend sends the full serialized row and we want the caregiver's cache to reflect
 * the new `questionnaire_needs_review` flag immediately.
 */
export const upsertConversation =
  (conversation: ConversationDTO) =>
  (previous: ConversationDTO[]): ConversationDTO[] => {
    if (previous.some(c => c.id === conversation.id)) {
      return previous.map(c => (c.id === conversation.id ? conversation : c));
    }
    return [conversation, ...previous];
  };

/**
 * If `messageId` matches the `last_message.id` of the conversation with `conversationId`,
 * apply `updates` to that last_message in place.  No-op otherwise (so it is safe to call
 * for every edit/delete event regardless of whether the message is actually the last one).
 */
export const updateConversationLastMessageIfMatch =
  (conversationId: string, messageId: number, updates: Partial<ConversationMessage>) =>
  (previous: ConversationDTO[]): ConversationDTO[] =>
    previous.map(conversation => {
      if (conversation.id !== conversationId) return conversation;
      if (!conversation.last_message || conversation.last_message.id !== messageId) return conversation;
      return {
        ...conversation,
        last_message: { ...conversation.last_message, ...updates },
      };
    });

/**
 * Synthesize a minimal row from a websocket `unassigned_message` payload (which only
 * gives us a message + sender = patient). Used by the caregiver-side handler to keep
 * the inbox in sync without a full re-fetch.
 */
export const insertUnassignedConversationFromMessage =
  (data: {
    conversationId: string;
    sender: UserDTO;
    messageText: string;
    repliedToMessage?: RepliedToMessage;
    messageId?: number;
  }) =>
  (previous: ConversationDTO[]): ConversationDTO[] => {
    const { conversationId, sender, messageText, repliedToMessage, messageId } = data;
    const sentAt = new Date().toISOString();
    const lastMessage: ConversationMessage = {
      id: messageId ?? Date.now(),
      message_text: messageText,
      sender,
      sent_at: sentAt,
      replied_to_message: repliedToMessage,
    };
    const existing = previous.find(c => c.id === conversationId);
    if (existing) {
      // Already in the cache — just refresh its last_message so the row jumps to the
      // top and shows the latest content.
      return previous.map(c => (c.id === conversationId ? { ...c, last_message: lastMessage } : c));
    }
    const synthesized: ConversationDTO = {
      id: conversationId,
      patient: sender,
      number_of_unread_messages: 0,
      last_message: lastMessage,
      status: { value: 'WAITING_FOR_FIRST_RESPONSE' },
    };
    return [synthesized, ...previous];
  };
